import { useMemo } from 'react';

const monoFont = "'IBM Plex Mono', monospace";

interface PasswordStrengthProps {
  password: string;
  /** 'dark' for CMS (white text on dark bg), 'light' for invite page (dark text on light bg) */
  theme?: 'dark' | 'light';
}

interface Criterion {
  label: string;
  met: boolean;
}

export function usePasswordCriteria(password: string) {
  return useMemo<Criterion[]>(() => [
    { label: 'Mínimo 8 caracteres', met: password.length >= 8 },
    { label: 'Letra maiúscula (A–Z)', met: /[A-Z]/.test(password) },
    { label: 'Letra minúscula (a–z)', met: /[a-z]/.test(password) },
    { label: 'Número (0–9)', met: /[0-9]/.test(password) },
    { label: 'Caractere especial (!@#$%...)', met: /[!@#$%^&*()_+\-=\[\]{}|;':",.<>?]/.test(password) },
  ], [password]);
}

export function allCriteriaMet(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*()_+\-=\[\]{}|;':",.<>?]/.test(password)
  );
}

const strengthLevels = [
  { max: 2, color: '#ff4444', label: 'Fraca' },
  { max: 3, color: '#facc15', label: 'Média' },
  { max: 4, color: '#60a5fa', label: 'Boa' },
  { max: 5, color: '#4ade80', label: 'Forte' },
];

export function PasswordStrength({ password, theme = 'dark' }: PasswordStrengthProps) {
  const criteria = usePasswordCriteria(password);
  const metCount = criteria.filter(c => c.met).length;

  const inactiveColor = theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
  const activeColor = '#4ade80';

  const level = metCount === 0
    ? null
    : strengthLevels.find(l => metCount <= l.max) || strengthLevels[strengthLevels.length - 1];

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Criteria header */}
      <div style={{ fontFamily: monoFont, fontSize: 11, fontWeight: 300, color: inactiveColor, marginBottom: 8 }}>
        A senha deve conter:
      </div>

      {/* Checklist */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        {criteria.map((c) => (
          <div
            key={c.label}
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              fontWeight: 300,
              color: c.met ? activeColor : inactiveColor,
              transition: 'color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>{c.met ? '✓' : '○'}</span>
            <span>{c.label}</span>
          </div>
        ))}
      </div>

      {/* Strength bar */}
      {password.length > 0 && level && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 3, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
            <div
              style={{
                height: '100%',
                width: `${(metCount / 5) * 100}%`,
                backgroundColor: level.color,
                transition: 'width 0.3s ease, background-color 0.3s ease',
              }}
            />
          </div>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              fontWeight: 300,
              color: level.color,
              transition: 'color 0.3s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {level.label}
          </span>
        </div>
      )}
    </div>
  );
}
